import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import type {
  Conversation,
  ConversationListResponse,
  Message,
  MessageListResponse,
  UnreadCountResponse,
  MarkAsReadResponse,
  SendTextMessageRequest,
} from '../types/messaging'

interface ApiResponse<T> {
  message: string
  data: T
}

const PAGE_SIZE = 30

export const messagingQueryKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingQueryKeys.all, 'conversations'] as const,
  conversation: (orderId: string) => [...messagingQueryKeys.all, 'conversation', orderId] as const,
  messages: (orderId: string) => [...messagingQueryKeys.all, 'messages', orderId] as const,
  unreadCount: () => [...messagingQueryKeys.all, 'unread-count'] as const,
}

// Get all conversations for seller
export const useGetConversations = () => {
  return useQuery({
    queryKey: messagingQueryKeys.conversations(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ConversationListResponse>>(
        'seller/conversations'
      )
      return data.data
    },
  })
}

// Get conversation for a specific order
export const useGetConversation = (orderId: string) => {
  return useQuery({
    queryKey: messagingQueryKeys.conversation(orderId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<Conversation>>(
        `seller/conversations/${orderId}`
      )
      return data.data
    },
    enabled: !!orderId,
  })
}

// Open or get existing conversation
export const useOpenConversation = (orderId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request?: { initialMessage?: string }) => {
      const { data } = await apiClient.post<ApiResponse<Conversation>>(
        `seller/conversations/${orderId}/open`,
        request || {}
      )
      return data.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(messagingQueryKeys.conversation(orderId), data)
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.conversations() })
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.unreadCount() })
    },
  })
}

// Get messages for conversation
export const useGetMessages = (orderId: string) => {
  return useInfiniteQuery({
    queryKey: messagingQueryKeys.messages(orderId),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = { limit: PAGE_SIZE }
      if (pageParam) {
        params.after = pageParam
      }

      const { data } = await apiClient.get<ApiResponse<MessageListResponse>>(
        `seller/conversations/${orderId}/messages`,
        { params }
      )
      return data.data
    },
    enabled: !!orderId,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor || null,
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      messages: data.pages.flatMap((page) => page?.messages || []).reverse(),
    }),
  })
}

// Send text message
export const useSendTextMessage = (orderId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: SendTextMessageRequest) => {
      const { data } = await apiClient.post<ApiResponse<Message>>(
        `seller/conversations/${orderId}/messages`,
        request
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.messages(orderId) })
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.conversation(orderId) })
    },
  })
}

// Send audio message
export const useSendAudioMessage = (orderId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ audioFile, duration }: { audioFile: Blob; duration: number }) => {
      const formData = new FormData()
      formData.append('audio', audioFile, 'audio.webm')
      formData.append('duration', duration.toString())

      const { data } = await apiClient.post<ApiResponse<Message>>(
        `seller/conversations/${orderId}/messages/audio`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.messages(orderId) })
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.conversation(orderId) })
    },
  })
}

// Send image message
export const useSendImageMessage = (orderId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ imageFile, caption }: { imageFile: Blob; caption?: string }) => {
      const formData = new FormData()
      formData.append('image', imageFile, 'image.jpg')
      if (caption) {
        formData.append('caption', caption)
      }

      const { data } = await apiClient.post<ApiResponse<Message>>(
        `seller/conversations/${orderId}/messages/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.messages(orderId) })
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.conversation(orderId) })
    },
  })
}

// Mark messages as read
export const useMarkAsRead = (orderId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (messageIds?: string[]) => {
      const { data } = await apiClient.patch<ApiResponse<MarkAsReadResponse>>(
        `seller/conversations/${orderId}/messages/read`,
        messageIds && messageIds.length > 0 ? { messageIds } : {}
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.conversation(orderId) })
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.conversations() })
      queryClient.invalidateQueries({ queryKey: messagingQueryKeys.unreadCount() })
    },
  })
}

// Get unread count
export const useUnreadCount = () => {
  return useQuery({
    queryKey: messagingQueryKeys.unreadCount(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<UnreadCountResponse>>(
        'seller/conversations/unread-count'
      )
      return data.data
    },
    refetchInterval: 60000,
  })
}
